import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "cmd__panel",
  role: "dialog",
  "aria-modal": "true",
  "aria-label": "Recherche"
}
const _hoisted_2 = { class: "cmd__search" }
const _hoisted_3 = ["onKeydown"]
const _hoisted_4 = { class: "cmd__group-title" }
const _hoisted_5 = ["onClick", "onMousemove"]
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = {
  key: 1,
  class: "cmd__empty"
}

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { docsNav } from './docs/nav';
import { useCommandPalette } from '../composables/useCommandPalette';

interface IndexedItem {
  label: string;
  to: string;
  index: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CommandPalette',
  setup(__props) {

const router = useRouter();
const { open, openPalette, closePalette } = useCommandPalette();

const query = ref('');
const activeIndex = ref(0);
const inputRef = ref<HTMLInputElement | null>(null);
const resultsRef = ref<HTMLElement | null>(null);

const filteredGroups = computed(() => {
  const q = query.value.trim().toLowerCase();
  const groups: { title: string; items: IndexedItem[] }[] = [];
  let idx = 0;

  for (const group of docsNav) {
    const items = group.items
      .filter((it) => it.label.toLowerCase().includes(q))
      .map((it) => ({ label: it.label, to: it.to, index: idx++ }));
    if (items.length) groups.push({ title: group.title, items });
  }

  return groups;
});

const flat = computed(() => filteredGroups.value.flatMap((g) => g.items));

watch(query, () => {
  activeIndex.value = 0;
});

watch(open, async (isOpen) => {
  document.body.style.overflow = isOpen ? 'hidden' : '';
  if (isOpen) {
    query.value = '';
    activeIndex.value = 0;
    await nextTick();
    inputRef.value?.focus();
  }
});

function move(dir: number) {
  const n = flat.value.length;
  if (!n) return;
  activeIndex.value = (activeIndex.value + dir + n) % n;
  nextTick(() => {
    const el = resultsRef.value?.querySelector('.cmd__item--active');
    (el as HTMLElement | null)?.scrollIntoView({ block: 'nearest' });
  });
}

function selectActive() {
  const item = flat.value[activeIndex.value];
  if (item) go(item.to);
}

function go(to: string) {
  closePalette();
  router.push(to);
}

function close() {
  closePalette();
}

function escapeHtml(value: string) {
  return value.replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string,
  );
}

function highlight(label: string) {
  const q = query.value.trim();
  if (!q) return escapeHtml(label);
  const i = label.toLowerCase().indexOf(q.toLowerCase());
  if (i === -1) return escapeHtml(label);
  return (
    escapeHtml(label.slice(0, i)) +
    '<mark>' +
    escapeHtml(label.slice(i, i + q.length)) +
    '</mark>' +
    escapeHtml(label.slice(i + q.length))
  );
}

function onKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    open.value ? closePalette() : openPalette();
  } else if (e.key === 'Escape' && open.value) {
    closePalette();
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = '';
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "cmd" }, {
      default: _withCtx(() => [
        (_unref(open))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "cmd",
              onClick: _withModifiers(close, ["self"])
            }, [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _cache[3] || (_cache[3] = _createElementVNode("svg", {
                    class: "cmd__search-icon",
                    viewBox: "0 0 24 24",
                    width: "18",
                    height: "18",
                    fill: "none",
                    stroke: "currentColor",
                    "stroke-width": "2",
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "aria-hidden": "true"
                  }, [
                    _createElementVNode("circle", {
                      cx: "11",
                      cy: "11",
                      r: "8"
                    }),
                    _createElementVNode("path", { d: "m21 21-4.3-4.3" })
                  ], -1 /* CACHED */)),
                  _withDirectives(_createElementVNode("input", {
                    ref_key: "inputRef",
                    ref: inputRef,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
                    class: "cmd__input",
                    type: "text",
                    placeholder: "Search documentation...",
                    "aria-label": "Rechercher dans la documentation",
                    onKeydown: [
                      _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (move(1)), ["prevent"]), ["down"])),
                      _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (move(-1)), ["prevent"]), ["up"])),
                      _withKeys(_withModifiers(selectActive, ["prevent"]), ["enter"])
                    ]
                  }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_3), [
                    [_vModelText, query.value]
                  ])
                ]),
                _createElementVNode("div", {
                  ref_key: "resultsRef",
                  ref: resultsRef,
                  class: "cmd__results"
                }, [
                  (flat.value.length)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(filteredGroups.value, (group) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: group.title,
                          class: "cmd__group"
                        }, [
                          _createElementVNode("p", _hoisted_4, _toDisplayString(group.title), 1 /* TEXT */),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.items, (item) => {
                            return (_openBlock(), _createElementBlock("button", {
                              key: item.to,
                              type: "button",
                              class: _normalizeClass(["cmd__item", { 'cmd__item--active': item.index === activeIndex.value }]),
                              onClick: ($event: any) => (go(item.to)),
                              onMousemove: ($event: any) => (activeIndex.value = item.index)
                            }, [
                              _cache[4] || (_cache[4] = _createElementVNode("span", {
                                class: "cmd__arrow",
                                "aria-hidden": "true"
                              }, "→", -1 /* CACHED */)),
                              _createElementVNode("span", {
                                innerHTML: highlight(item.label)
                              }, null, 8 /* PROPS */, _hoisted_6)
                            ], 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_5))
                          }), 128 /* KEYED_FRAGMENT */))
                        ]))
                      }), 128 /* KEYED_FRAGMENT */))
                    : (_openBlock(), _createElementBlock("p", _hoisted_7, "Aucun résultat."))
                ], 512 /* NEED_PATCH */),
                _cache[5] || (_cache[5] = _createElementVNode("div", { class: "cmd__footer" }, [
                  _createElementVNode("kbd", { class: "cmd__kbd" }, "↵"),
                  _createElementVNode("span", null, "Aller à la page")
                ], -1 /* CACHED */))
              ])
            ]))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    })
  ]))
}
}

})